import {
  AlloyButton,
  Checkbox,
  CheckboxGroup,
  Popup
} from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { deleteLineItemBundle } from '@app/store/orders/actions';
import ExceptionFeedback from '@app/components/common/Exception/ExceptionFeedback';
import ContactDealerPopup from '@app/components/common/ContactDealerPopup';
import { replaceTokensInString, replaceTokensWithComponents } from '@app/utils';
import { getAddAndRemoveCartEvents } from '../../OrderItemsTable/utils';
import { LIST_PCC_CART_PAGE } from '@app/constants/analyticsConstants';
import useAnalytics from '@app/hooks/useAnalytics/useAnalytics';

const RemoveBundleModal = ({
  orderItemId,
  associatedBundleCount,
  ssroBundle,
  bundlesToRemoveCount,
  closeModal
}) => {
  const [ssroIdList, setSsroIdList] = useState([]);
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { isLineItemDeleteFailed } = useSelector(s => s.orders);
  const storeName = useSelector(s => s.store?.name) || '';
  const dealerStore = useSelector(s => s.common?.storeLocationName) ?? '';
  const { fireRemoveFromCartEvent, fireProductClickEvent } = useAnalytics();

  const trackGA = useCallback(
    updatedItems => {
      const removedKitIds = ssroIdList.map(id => `SSO-${id}`);
      updatedItems.forEach(
        item =>
          (item.orderItemExtendAttribute.googleAnalyticsAttributes.kitId =
            removedKitIds.join(','))
      );
      const { removeFromCartItems } = getAddAndRemoveCartEvents(
        updatedItems,
        storeName
      );
      if (removeFromCartItems.length) {
        fireProductClickEvent({
          list: LIST_PCC_CART_PAGE,
          products: removeFromCartItems,
          dealerStore
        });
        fireRemoveFromCartEvent({ products: removeFromCartItems, dealerStore });
      }
    },
    [storeName, fireProductClickEvent, fireRemoveFromCartEvent, ssroIdList]
  );

  const ssroBundles = () =>
    ssroBundle.map(bundle => (
      <Checkbox
        key={bundle.selfServiceOptionId}
        id={bundle.selfServiceOptionId}
        data-testid={bundle.selfServiceOptionId}
        className="mb-2"
        label={
          <p className="u-color--DarkGray text-md">
            {`${bundle.selfServiceOptionName}`}
          </p>
        }
        value={bundle.selfServiceOptionId}
      />
    ));

  const handleChange = e => {
    const { value, checked } = e.target;
    checked
      ? setSsroIdList(vs => vs.concat(value).sort((a, b) => a - b))
      : setSsroIdList(vs => vs.filter(v => v !== value));
  };

  const getBundleStateText = () => {
    if (ssroIdList.length === 0) {
      return replaceTokensInString(
        t('SELECT_BUNDLES_CONTINUE'),
        bundlesToRemoveCount - ssroIdList.length
      );
    } else if (ssroIdList.length < bundlesToRemoveCount) {
      return replaceTokensInString(
        t('SELECT_MORE_BUNDLES_CONTINUE'),
        bundlesToRemoveCount - ssroIdList.length
      );
    } else {
      return t('READY_PROCEED');
    }
  };

  const removeBundle = () => {
    dispatch(
      deleteLineItemBundle(orderItemId, ssroIdList, closeModal, t, trackGA)
    );
  };

  const getDealerInitiator = () => (
    <a className="text-decoration-none">{t('GENERICERR_CONTACT_US')}</a>
  );

  const showContactDealerPopup = () => {
    return (
      <Popup
        contents={<ContactDealerPopup hideContactUs />}
        initiator={getDealerInitiator()}
        hideCloseButton
        className={'mt-2'}
      />
    );
  };

  return (
    <div>
      {isLineItemDeleteFailed && (
        <ExceptionFeedback>
          <div className="pb-2">
            {replaceTokensWithComponents(
              t('UNKONWN_ERROR_LINK'),
              showContactDealerPopup()
            )}
          </div>
        </ExceptionFeedback>
      )}
      <p className="u-color--DarkGray text-sm pt-2">
        {replaceTokensInString(
          t('SSRO_REMOVE_BUNDLE_MODAL'),
          associatedBundleCount,
          bundlesToRemoveCount
        )}
      </p>
      <p className="u-color--DarkGray text-sm pt-3">
        {t('ASSOCIATE_BUNDLE_TO_PART')}
      </p>
      <section className="mt-3 pb-2 pb-md-3">
        <span className="u-color--DarkGray text-sm fw-bold">
          {getBundleStateText()}
        </span>
      </section>
      <section className="ps-2">
        <CheckboxGroup onChange={handleChange}>{ssroBundles()}</CheckboxGroup>
      </section>
      <div className="d-flex flex-column flex-md-row">
        <AlloyButton
          buttonType="secondary"
          disabled={ssroIdList.length < bundlesToRemoveCount}
          size="dynamic"
          onClick={removeBundle}
          className="me-2 mb-2 mb-md-0"
        >
          {t('REMOVE_BUNDLE')}
        </AlloyButton>
        <AlloyButton buttonType="ghost" size="dynamic" onClick={closeModal}>
          {t('CANCEL')}
        </AlloyButton>
      </div>
    </div>
  );
};

RemoveBundleModal.propTypes = {
  ssroBundle: PropTypes.arrayOf(
    PropTypes.shape({
      selfServiceOptionId: PropTypes.string,
      selfServiceOptionName: PropTypes.string
    })
  ),
  orderItemId: PropTypes.string,
  associatedBundleCount: PropTypes.number,
  bundlesToRemoveCount: PropTypes.number,
  closeModal: PropTypes.func.isRequired
};
export default RemoveBundleModal;
